import React, { useState } from 'react';
import { emailSearch, emailForward } from 'utils/api.service';

const SUBJECT_SUGGESTIONS = [
  'Your Autodesk quote is ready',
  'Your Autodesk order confirmation',
  'One-time passcode',
  'Past Due Notice - Autodesk Invoice',
  'Invitation to create your Autodesk account',
  'Access to Autodesk products has been cancelled',
  'Zero tokens remaining for Autodesk Flex',
];

const DISPLAY_OPTIONS = [
  { value: 'latest', label: 'Latest' },
  { value: 'last5', label: 'Last 5' },
  { value: 'last10', label: 'Last 10' },
  { value: 'last30', label: 'Last 30' },
  { value: 'last50', label: 'Last 50' },
];

export default function EmailSearch() {
  const [subject, setSubject] = useState('');
  const [toEmail, setToEmail] = useState('');
  const [displayMode, setDisplayMode] = useState('latest');
  const [results, setResults] = useState([]);
  const [flashMessages, setFlashMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [forwardLoading, setForwardLoading] = useState({}); // { [message_id]: boolean }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFlashMessages([]);
    setResults([]);
    setLoading(true);
    try {
      const res = await emailSearch(subject, toEmail, displayMode);
      if (res && res.data && Array.isArray(res.data.results)) {
        setResults(res.data.results);
        if (res.data.results.length === 0) {
          setFlashMessages([
            { category: 'info', message: 'No messages found.' },
          ]);
        }
      } else {
        setResults([]);
      }
    } catch (err) {
      setFlashMessages([
        {
          category: 'danger',
          message: err?.response?.data?.message || 'Search failed.',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleForward = async (message_id, to_email) => {
    setFlashMessages([]);
    setForwardLoading((prev) => ({ ...prev, [message_id]: true }));
    try {
      await emailForward(message_id, to_email);
      setFlashMessages([
        { category: 'success', message: 'Email forwarded successfully!' },
      ]);
    } catch (err) {
      setFlashMessages([
        {
          category: 'danger',
          message: err?.response?.data?.message || 'Forward failed.',
        },
      ]);
    } finally {
      setForwardLoading((prev) => ({ ...prev, [message_id]: false }));
    }
  };

  return (
    <div className="container-fluid py-4">
      <h2>Search Email For Test User</h2>
      <div>
        <em
          style={{ fontSize: '0.9rem', color: '#6c757d', marginBottom: '1rem' }}
        >
          Note: This tool only searches email with domain ending with
          @letscheck.dev or @letscheck.pw
        </em>
      </div>
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div
              className="card-header"
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <i className="bi bi-envelope"></i>
              <h5 className="mb-0" style={{ margin: 0, fontFamily: 'inherit' }}>
                Email Search
              </h5>
            </div>
            <div className="card-body">
              {/* Flash Messages */}
              {flashMessages.length > 0 && (
                <div className="flash-messages">
                  {flashMessages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`alert alert-${msg.category} alert-dismissible`}
                      role="alert"
                    >
                      <button
                        type="button"
                        className="close"
                        data-dismiss="alert"
                        aria-label="Close"
                      >
                        &times;
                      </button>
                      <i
                        className={`bi ${msg.category === 'success'
                            ? 'bi-check-circle'
                            : 'bi-exclamation-circle'
                          }`}
                        style={{ marginRight: '8px' }}
                        aria-hidden="true"
                      ></i>
                      {msg.message}
                    </div>
                  ))}
                </div>
              )}

              {/* Search Form */}
              <div className="card mb-4">
                <div className="card-body">
                  <form className="search-form" onSubmit={handleSubmit}>
                    <div className="row g-3 align-items-end">
                      {/* Email Subject */}
                      <div className="col-md-3">
                        <label htmlFor="email_type" className="form-label">
                          Email Subject
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="email_type"
                          name="email_type"
                          value={subject}
                          onChange={(e) => setSubject(e.target.value)}
                          placeholder="Enter email subject"
                          list="subject-suggestions"
                        />
                        <datalist id="subject-suggestions">
                          {SUBJECT_SUGGESTIONS.map((s, i) => (
                            <option value={s} key={i} />
                          ))}
                        </datalist>
                      </div>
                      {/* Recipient (To) */}
                      <div className="col-md-4">
                        <label htmlFor="email_id" className="form-label">
                          Recipient Email Id
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          id="email_id"
                          name="email_id"
                          value={toEmail}
                          onChange={(e) => setToEmail(e.target.value)}
                          placeholder="Enter email address"
                          required
                        />
                      </div>
                      {/* Display Count */}
                      <div className="col-md-2">
                        <label htmlFor="display_count" className="form-label">
                          Display
                        </label>
                        <select
                          className="form-control"
                          id="display_count"
                          name="display_count"
                          value={displayMode}
                          onChange={(e) => setDisplayMode(e.target.value)}
                        >
                          {DISPLAY_OPTIONS.map((opt) => (
                            <option value={opt.value} key={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      {/* Submit Button */}
                      <div className="col-md-2 d-flex align-items-end">
                        <button
                          type="submit"
                          className="btn btn-primary w-100"
                          disabled={loading}
                        >
                          Search
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>

              {/* Loading Spinner */}
              {loading && (
                <div className="d-flex flex-column align-items-center my-4">
                  <div
                    className="spinner-border text-primary mb-2"
                    style={{ width: '3rem', height: '3rem' }}
                    role="status"
                  />
                  <div>Searching...</div>
                </div>
              )}

              {/* Results Section */}
              {!loading && results.length > 0 && (
                <div className="results-section">
                  <h6 className="mb-3">Search Results</h6>
                  {results.map((data, idx) => (
                    <div className="card mb-3" key={idx}>
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <div>
                            <h6 className="card-subtitle mb-2">
                              Subject: {data.subject}
                            </h6>
                            <h6 className="card-subtitle mb-2 text-muted">
                              From: {data.from}
                            </h6>
                            <h6 className="card-subtitle mb-2 text-muted">
                              To: {data.to}
                            </h6>
                            <h6 className="card-subtitle mb-2 text-muted">
                              CC: {data.cc}
                            </h6>
                            <h6 className="card-subtitle mb-2 text-muted">
                              Date: {data.date}
                            </h6>
                          </div>
                          {/* Forward Form */}
                          <div className="forward-section">
                            <form
                              className="d-inline"
                              onSubmit={async (e) => {
                                e.preventDefault();
                                const forwardTo = e.target.forward_to.value;
                                await handleForward(data.message_id, forwardTo);
                              }}
                            >
                              <div className="input-group forward-input-group">
                                <span className="input-group-text">
                                  <i className="bi bi-envelope"></i>
                                </span>
                                <input
                                  type="email"
                                  className="form-control"
                                  name="forward_to"
                                  placeholder="Enter recipient email"
                                  required
                                  disabled={forwardLoading[data.message_id]}
                                />
                                <button
                                  type="submit"
                                  className="btn btn-primary forward-btn"
                                  disabled={forwardLoading[data.message_id]}
                                >
                                  {forwardLoading[data.message_id] ? (
                                    <>
                                      <span
                                        className="spinner-border spinner-border-sm me-1"
                                        role="status"
                                        aria-hidden="true"
                                      ></span>
                                      Forwarding...
                                    </>
                                  ) : (
                                    <>
                                      <i className="bi bi-forward-fill me-1"></i>{' '}
                                      Forward
                                    </>
                                  )}
                                </button>
                              </div>
                            </form>
                          </div>
                        </div>
                        <div className="email-body">
                          {data.email_body ? (
                            <span
                              dangerouslySetInnerHTML={{
                                __html: data.email_body,
                              }}
                            />
                          ) : (
                            <pre>{data.body}</pre>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
